/**
 * 非矢量笔刷的临时 Canvas 宿主，挂载于视口插件内
 * 使用命令式句柄接收笔迹，并订阅编辑器平移/缩放及容器尺寸变化；最终图层渲染独立于此组件
 */
import React, { useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { getFramework } from '@/framework';
import { fetchVectorText, getVectorTextFromCache } from '@/utils/file-kit';
import type { IBrushDrawConfig, ILayerProps } from '@/version-types';
import { paintBrushPreview } from './brush-preview';

/** 非矢量笔刷可调用的瞬态绘制能力 */
export interface BrushPreviewHandle {
    /**
     * 清空并重绘临时 Canvas，同时保存待重放笔迹
     * @param layer - 使用全局场景位置的临时笔刷，尚未转换为目标画布的页内坐标
     * @returns 是否绘制成功，素材就绪后会自动重放最新笔迹
     */
    draw(layer: ILayerProps): boolean;
    /**
     * 清空临时画布和上一次预览数据，停止平移缩放时的重放
     */
    clear(): void;
}

/**
 * 在画笔插件内挂载视口预览，依赖当前笔刷配置和编辑器的平移缩放状态
 *
 * @param props - 编辑器标识与需要预加载素材的笔刷配置
 * @param ref - 接收同步绘制和清空笔迹的命令式句柄
 * @returns 仅用于绘制过程的 Canvas 节点
 */
const BrushPreviewCanvas = React.forwardRef<BrushPreviewHandle, { identity: string; config: IBrushDrawConfig }>(
    ({ identity, config }, ref) => {
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const layerRef = useRef<ILayerProps | null>(null);
        const service = getFramework(identity).service;
        const clearPixels = useCallback(() => {
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d');
            if (canvas && ctx) {
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        }, []);
        const clear = useCallback(() => {
            layerRef.current = null;
            clearPixels();
        }, [clearPixels]);
        const draw = useCallback(
            (layer: ILayerProps) => {
                const canvas = canvasRef.current;
                if (!canvas) return false;
                const container = canvas.parentElement;
                if (!container) return false;
                // 素材请求期间只保留最新笔迹，不排队生成过期位图。
                layerRef.current = layer;
                const { zoom, translateX, translateY, retinaScaling } = service.edgelessCanvas;
                try {
                    const painted = paintBrushPreview(canvas, layer, {
                        width: container.clientWidth,
                        height: container.clientHeight,
                        zoom,
                        x: translateX,
                        // 临时位置已包含长画布的全局偏移；默认 __originIndex 在页面重排后不能代表绘制页。
                        y: translateY,
                        retina: retinaScaling,
                    });
                    if (!painted) {
                        clearPixels();
                        return false;
                    }
                    return true;
                } catch {
                    // 绘制异常可能打断底层 save/restore；重置上下文避免残留裁剪影响下一笔。
                    canvas.width = 0;
                    clearPixels();
                    return false;
                }
            },
            [clearPixels, service],
        );
        useImperativeHandle(ref, () => ({ draw, clear }), [draw, clear]);
        useLayoutEffect(() => {
            let active = true;
            const urls = [config.url, config.startPoint?.url, config.endPoint?.url];
            const missing = [...new Set(urls.filter((url) => url && !getVectorTextFromCache(url)))];
            if (missing.length) {
                Promise.all(missing.map((url) => fetchVectorText(url)))
                    .then(() => {
                        if (active && layerRef.current) draw(layerRef.current);
                    })
                    .catch(() => {
                        // 与原预览一致，素材失败不改变已绘制数据或最终落图流程。
                    });
            }
            return () => {
                active = false;
            };
        }, [config, draw]);
        useLayoutEffect(() => {
            const refresh = () => {
                if (layerRef.current) draw(layerRef.current);
            };
            const zoomHandle = service.edgelessCanvas.onZoom(refresh);
            const panHandle = service.edgelessCanvas.onPan(refresh);
            const observer = new ResizeObserver(refresh);
            if (canvasRef.current?.parentElement) observer.observe(canvasRef.current.parentElement);
            return () => {
                observer.disconnect();
                service.events.off(zoomHandle);
                service.events.off(panHandle);
                layerRef.current = null;
            };
        }, [draw, service]);
        return <canvas ref={canvasRef} className="brush-canvas-preview" />;
    },
);
BrushPreviewCanvas.displayName = 'BrushPreviewCanvas';
export default BrushPreviewCanvas;
